import unescapedText from 'I18n/utils/unescapedText';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { isKnownErrorType, getAuthErrorI18nKeys } from '../components/LowLevelAuthFlowView/LowLevelAuthErrorAlert';

/**
 * Surfaces a headless auth failure. When the consumer provides an `onAuthError`
 * callback we defer to it; otherwise we show a floating alert so the failure is
 * never silent (the headless flow has no modal to render the error in).
 *
 * The alert carries the same context as the modal error surface
 * (`LowLevelAuthErrorAlert`): when the backend supplies a message we show it
 * verbatim instead of the generic per-type copy, and any upstream detail is
 * rendered inline under an "Error details" label. A floating alert has no room
 * for a collapsible, so the detail is shown inline rather than in an accordion.
 * The generic per-type copy is used only when the error carries no message.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function reportHeadlessAuthError(error, appName, onError) {
  if (onError) {
    onError(error);
    return;
  }
  const keys = isKnownErrorType(error.type) ? getAuthErrorI18nKeys(error.type) : getAuthErrorI18nKeys('AuthenticateError');

  // Backend-originated errors (AuthorizeError / AuthenticateError and custom
  // errors) carry a localized `message`; AuthenticateError may additionally
  // carry an `upstreamErrorMessage` detail. Only some union members declare
  // them, so narrow with `in` rather than accessing them on the union.
  const backendMessage = 'message' in error ? error.message : undefined;
  const details = 'upstreamErrorMessage' in error ? error.upstreamErrorMessage : undefined;
  const body = backendMessage || (keys.messageUsesAppName ? unescapedText(keys.messageKey, {
    appName
  }) : unescapedText(keys.messageKey));
  const message = details ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      children: body
    }), /*#__PURE__*/_jsxs("div", {
      style: {
        marginBlockStart: '8px'
      },
      children: [/*#__PURE__*/_jsx("strong", {
        children: unescapedText('unifiedAuthCore.errors.accountCreation.details')
      }), /*#__PURE__*/_jsx("div", {
        style: {
          whiteSpace: 'pre-line'
        },
        children: details
      })]
    })]
  }) : body;

  // No fixed `id`: let the store assign a fresh one per call so a second
  // failure (rapid retry, or two concurrent headless flows) is not deduped away
  // or silently replaced.
  FloatingAlertStore.addAlert({
    type: 'danger',
    titleText: unescapedText(keys.titleKey, {
      appName
    }),
    message
  });
}